export const DEFAULT_EVENT_IMAGE = "/img/cover/cover_calendar_france.jpeg";

export const DEFAULT_SHARED_EVENT_FILTERS = {
  search: "",
  departement: "",
  type_event: "",
  cate_event: "",
  level_event: "",
  time_event: "upcoming",
};

export const SHARED_EVENT_TYPES = [
  "Animation",
  "Compétition",
  "Stage",
  "Tournoi Interclub",
  "Tournoi Interclubs",
  "UV1 Kata",
  "UV2 Technique",
  "UV3 Shiai",
  "UV4 Arbitrage",
];

export const SHARED_EVENT_CATEGORIES = [
  "Eveils",
  "Mini-Poussins",
  "Poussins",
  "Benjamins",
  "Minimes",
  "Cadets",
  "Juniors",
  "Séniors",
  "Vétérans",
];

export const SHARED_EVENT_LEVELS = [
  "Club",
  "Départemental",
  "Régional",
  "National",
  "International",
];

export function safeDate(value) {
  if (!value) return null;

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function todayAtMidnight() {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return now;
}

export function formatEventDay(value) {
  const date = safeDate(value);
  if (!date) return "--";

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
  }).format(date);
}

export function formatEventMonth(value) {
  const date = safeDate(value);
  if (!date) return "---";

  return new Intl.DateTimeFormat("fr-FR", {
    month: "short",
  })
    .format(date)
    .replace(".", "");
}

export function formatEventYear(value) {
  const date = safeDate(value);
  if (!date) return "";

  return new Intl.DateTimeFormat("fr-FR", {
    year: "numeric",
  }).format(date);
}

export function formatEventDate(value) {
  const date = safeDate(value);
  if (!date) return "Date non renseignée";

  return new Intl.DateTimeFormat("fr-FR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(date);
}

export function normalizeText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

export function getEventDepartment(event) {
  return (
    event?.Adherents?.[0]?.departement ||
    event?.Adherent?.departement ||
    event?.departement ||
    ""
  );
}

export function getEventCategories(event) {
  if (Array.isArray(event?.cate_event)) {
    return event.cate_event.map((item) => String(item).trim()).filter(Boolean);
  }

  return String(event?.cate_event || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

export function isUpcomingEvent(event) {
  const date = safeDate(event?.start);
  if (!date) return false;

  return date >= todayAtMidnight();
}

export function sortSharedEvents(events = []) {
  return [...events].sort((a, b) => {
    const dateA = safeDate(a?.start)?.getTime() || 0;
    const dateB = safeDate(b?.start)?.getTime() || 0;

    return dateA - dateB;
  });
}

export function filterSharedEvents(events = [], filters = DEFAULT_SHARED_EVENT_FILTERS) {
  const now = todayAtMidnight();
  const search = normalizeText(filters.search);

  return sortSharedEvents(events).filter((event) => {
    const eventDate = safeDate(event?.start);
    const department = getEventDepartment(event);
    const categories = getEventCategories(event);

    const matchesTime =
      filters.time_event === "all" ||
      (filters.time_event === "upcoming" && eventDate && eventDate >= now) ||
      (filters.time_event === "past" && eventDate && eventDate < now);

    const matchesDepartment =
      !filters.departement || department === filters.departement;

    const matchesType =
      !filters.type_event || event?.type_event === filters.type_event;

    const matchesLevel =
      !filters.level_event || event?.level_event === filters.level_event;

    const matchesCategory =
      !filters.cate_event || categories.includes(filters.cate_event);

    const searchableContent = normalizeText(
      [
        event?.titre,
        event?.description,
        event?.type_event,
        event?.level_event,
        event?.lieu_event,
        event?.horaire_event,
        department,
        categories.join(" "),
      ].join(" "),
    );

    const matchesSearch = !search || searchableContent.includes(search);

    return (
      matchesTime &&
      matchesDepartment &&
      matchesType &&
      matchesLevel &&
      matchesCategory &&
      matchesSearch
    );
  });
}

export function countActiveFilters(filters = DEFAULT_SHARED_EVENT_FILTERS) {
  return Object.entries(filters).reduce((count, [key, value]) => {
    if (key === "time_event" && value === "upcoming") return count;
    if (!value) return count;

    return count + 1;
  }, 0);
}

export function getSharedEventsStats(events = []) {
  const today = todayAtMidnight();

  const upcoming = events.filter((event) => {
    const date = safeDate(event?.start);
    return date && date >= today;
  }).length;

  const past = events.filter((event) => {
    const date = safeDate(event?.start);
    return date && date < today;
  }).length;

  const departments = new Set(
    events.map((event) => getEventDepartment(event)).filter(Boolean),
  );

  const types = new Set(events.map((event) => event?.type_event).filter(Boolean));

  return {
    total: events.length,
    upcoming,
    past,
    departmentsCount: departments.size,
    typesCount: types.size,
  };
}

export function getEventStatusLabel(event) {
  return isUpcomingEvent(event) ? "À venir" : "Passé";
}

export function getEventStatusClass(event) {
  if (isUpcomingEvent(event)) {
    return "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-200";
  }

  return "bg-slate-100 text-slate-600 dark:bg-white/10 dark:text-white/60";
}

export function getSharedEventKey(event, index) {
  const id = event?.id || event?.event_id || event?.EventId;

  if (id) {
    return `shared-event-${id}-${index}`;
  }

  return `shared-event-fallback-${index}`;
}

export function dedupeSharedEventsById(events = []) {
  const seen = new Set();

  return events.filter((event, index) => {
    const rawId = event?.id || event?.event_id || event?.EventId;

    if (!rawId) {
      return true;
    }

    const key = String(rawId);

    if (seen.has(key)) {
      console.warn(
        "[SharedEventsPage] Événement partagé dupliqué ignoré :",
        rawId,
        event,
      );
      return false;
    }

    seen.add(key);
    return true;
  });
}